import useRequest from "api/useRequest";
import { FC, useRef, useState } from "react";
import {
    Button,
    Loader,
    LoadingButton,
    Modal,
    ModalActions,
    ModalContent,
    ModalTitle,
    RequestMessage,
    Settings,
    SettingsItem,
} from "ui";
import { providersCreate, providersDefaults } from "../actions";
import { AxiosResponse } from "axios";
import IProvidersAll from "../interfaces";
import { Box, MenuItem, Select } from "@mui/material";
import GastroPayForm, {
    GastroPayModel,
} from "../forms/GastroPay/GastroPayForm";
import AlexSushiInvoiceForm, {
    AlexSushiInvoiceModel,
} from "../forms/AlexSushiInvoice/AlexSushiInvoiceForm";
import DatatransForm from "../forms/Datatrans/DatatransForm";
import BamboraCreate from "../forms/Bambora/types/BamboraCreate";
import DatatransCreate from "../forms/Datatrans/types/DatatransCreate";
import BamboraForm from "../forms/Bambora/BamboraForm";
import DinteroCreate from "../forms/Dintero/types/DinteroCreate";
import DinteroForm from "../forms/Dintero/DinteroForm";
import ElavonForm from "../forms/Elavon/ElavonForm";
import NetsForm from "../forms/Nets/NetsForm";
import NetsCreate from "../forms/Nets/types/NetsCreate";
import StripeCreate from "../forms/Stripe/types/StripeCreate";
import StripeForm from "../forms/Stripe/StripeForm";
import VippsCreate from "../forms/Vipps/types/VippsCreate";
import VippsForm from "../forms/Vipps/VippsForm";

type ProviderCreateModalProps = {
    project: string;
    prefix: string;
    onCloseModal: () => void;
};

const ProviderCreateModal: FC<ProviderCreateModalProps> = ({
    onCloseModal,
    project,
    prefix,
}) => {
    const formRef = useRef<any>();
    const [provider, setProvider] = useState<string>("");

    const { data, isLoading: isLoadingAll } = useRequest(
        providersDefaults(project, prefix),
    );

    const { isLoading, request, errors, message, status } = useRequest();

    const handleClose = () => {
        onCloseModal();
    };

    const handleSaveForm = (
        event: React.FormEvent<HTMLFormElement | HTMLButtonElement>,
    ) => {
        formRef.current?.onSubmit(event, true);
    };

    const handleSubmit = (
        data:
            | AlexSushiInvoiceModel
            | BamboraCreate
            | DatatransCreate
            | DinteroCreate
            | GastroPayModel
            | NetsCreate
            | StripeCreate
            | VippsCreate,
    ) => {
        request(
            providersCreate(
                project,
                prefix,
                provider,
                data,
                (r: AxiosResponse) => {
                    if (r.status === 201) {
                        onCloseModal();
                    }
                },
            ),
        );
    };

    const renderPaymentForm = () => {
        if (provider === "") return null;

        switch (provider) {
            case "alex_sushi_invoice":
                return (
                    <AlexSushiInvoiceForm
                        ref={formRef}
                        errors={errors}
                        loading={isLoading}
                        onSubmit={handleSubmit}
                    />
                );
            case "bambora":
                return (
                    <BamboraForm
                        ref={formRef}
                        errors={errors}
                        loading={isLoading}
                        onSubmit={handleSubmit}
                    />
                );
            case "datatrans":
                return (
                    <DatatransForm
                        ref={formRef}
                        errors={errors}
                        loading={isLoading}
                        onSubmit={handleSubmit}
                    />
                );
            case "dintero":
                return (
                    <DinteroForm
                        ref={formRef}
                        errors={errors}
                        loading={isLoading}
                        onSubmit={handleSubmit}
                    />
                );
            case "elavon_converge":
                return (
                    <ElavonForm
                        ref={formRef}
                        errors={errors}
                        loading={isLoading}
                        onSubmit={handleSubmit}
                    />
                );
            case "gastropay":
                return (
                    <GastroPayForm
                        ref={formRef}
                        errors={errors}
                        loading={isLoading}
                        onSubmit={handleSubmit}
                    />
                );
            case "nets_easy":
                return (
                    <NetsForm
                        ref={formRef}
                        errors={errors}
                        loading={isLoading}
                        onSubmit={handleSubmit}
                    />
                );
            case "stripe":
                return (
                    <StripeForm
                        ref={formRef}
                        errors={errors}
                        loading={isLoading}
                        onSubmit={handleSubmit}
                    />
                );
            case "vipps":
                return (
                    <VippsForm
                        ref={formRef}
                        errors={errors}
                        loading={isLoading}
                        onSubmit={handleSubmit}
                    />
                );
        }
    };

    if (isLoadingAll) return <Loader />;
    if (!data) return null;

    return (
        <Modal
            open
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { position: "fixed", top: 0 } }}
        >
            <ModalTitle onClose={() => onCloseModal()}>
                Add payment provider
            </ModalTitle>
            <ModalContent sx={{ pt: 0, px: 2 }}>
                <RequestMessage
                    message={message}
                    status={status}
                    sx={{
                        mb: 1,
                    }}
                />
                <Settings sx={{ mb: 2 }}>
                    <SettingsItem label="Payment provider">
                        <Select
                            fullWidth
                            size="small"
                            value={provider}
                            onChange={(e: any) => setProvider(e.target.value)}
                        >
                            {data.map((item: IProvidersAll) => {
                                return (
                                    <MenuItem
                                        key={`provider-${item.provider}`}
                                        value={item.provider}
                                    >
                                        {item.name}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </SettingsItem>
                </Settings>

                {renderPaymentForm()}
            </ModalContent>
            <ModalActions>
                <Box display="flex" width="100%" textAlign="right">
                    <Button
                        onClick={handleClose}
                        color="inherit"
                        variant="text"
                        sx={{ mr: 1 }}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        sx={{ flex: 1 }}
                        onClick={handleSaveForm}
                        loading={isLoading}
                    >
                        Save
                    </LoadingButton>
                </Box>
            </ModalActions>
        </Modal>
    );
};

export default ProviderCreateModal;
